import _ from 'lodash'
import { SEOCompletor, Metadata, TwitterCardTypes, TouchedFields } from '@/modules/seo/types'
import store from '@/store'
import Store from '@/modules/core/vuex/Store'
import { RootState } from '@/store/types'
import { Product } from '@/modules/product';
import Axios from 'axios'
import BaseSEOCompletor from '@/modules/seo/util/BaseSEOCompletor';

/**
 * Completador de SEO para productos
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export default class ProductSEOCompletor extends BaseSEOCompletor implements SEOCompletor {

    constructor (protected store: Store<RootState>) {
        super(store)
    }

    /**
     * Producto actual en edición
     */
    get product (): Product {
        return this.store.get('product.currentProduct', new Product())
    }

    /**
     * @inheritDoc
     */
    public complete (metadata: Metadata, currentLocale: string): Metadata {
        const metadataTranslation = metadata.translate(currentLocale)
        const productTranslation = this.product.translate(currentLocale)

        if (!metadataTranslation || !productTranslation) {
            return metadata
        }

        this.updateField(metadata, currentLocale, 'title', productTranslation.publicName)
        this.updateField(metadata, currentLocale, 'description', productTranslation.shortDescription)

        // Campos de OpenGraph
        this.updateField(metadata, currentLocale, 'opengraph.title', productTranslation.publicName)

        if (metadataTranslation.twitter.type !== TwitterCardTypes.None) {
            this.updateField(metadata, currentLocale, 'twitter.image', this.getProductImageURL(this.product))
        }

        return metadata
    }

    /**
     * @inheritDoc
     */
    public initFields (metadata: Metadata, touchedFields: Map<string, string[]>): Map<string, string[]> {
        const product = this.product

        const fields = metadata.translations.forEach(seoTranslation => {
            const productTranslation = product.translate(seoTranslation.locale)
            if (!productTranslation) {
                return
            }

            const f = this.compareFields(seoTranslation, productTranslation, {
                'title': 'publicName',
                'description': 'shortDescription',
                'opengraph.title': 'publicName',
            })

            touchedFields.set(seoTranslation.locale, f)
        })

        return touchedFields
    }

    /**
     * Obtiene URL de la imagen del producto o un string vacio si el producto
     * no tiene imagen
     *
     * @param product { string }
     */
    private getProductImageURL (product: Product): string {
        // Si el producto de Unigest no tiene imagen en base64 quiere decir que tampoco
        // existe una imagen en la web, no se devuelve ninguna url
        if (!product.image) {
            return ''
        }

        const baseURL = localStorage.getItem('config.products.imageBaseURL') || `${window.location.origin}/images`

        return `${baseURL}/${product.id}.jpg`
    }
}

export function buildProductSEOCompletor () {
    return new ProductSEOCompletor(store)
}
